import type { HexMap } from "./map.ts";
import {
  BIOME_COLORS,
  BIOME_LABELS,
  cellKey,
  hexDistance,
  neighbors,
  parseKey,
} from "./map.ts";
import type { ClockState } from "./clock.ts";
import { seasonOf } from "./clock.ts";
import {
  RESOURCE_IDS,
  RESOURCE_LABELS,
  formatNetDelta,
  realmResourceFlagYields,
  seasonNets,
  seasonNetsWithGrowth,
  type LedgerState,
  type ResourceId,
} from "./economy.ts";
import {
  BANNER_BASE_GARRISON,
  DEFAULT_DELAY_TURNS,
  LOYALTY_LABELS,
  OUTCOME_LABELS,
  REFUSE_FAVOR_HIT,
  alreadyCalledBannersThisTurn,
  alreadyLeviedThisTurn,
  freeAbleSeat,
  loyaltyBucket,
  pendingHornCount,
  seatArmsTotals,
  unreadHornCount,
  type CallOutcome,
  type CallState,
  type HornEntry,
} from "./call.ts";
import {
  TROOP_LABELS,
  TROOP_TYPES,
  TRAIN_EDGES,
  canAffordStocks,
  catapultCrewBound,
  demoteEdgesTo,
  edgeCrewMaa,
  edgeFromNeed,
  edgeFromPer,
  garrisonTotal,
  hostTotal,
  previewDemoteRefund,
  promoteEdgesFrom,
  scaleCost,
  type PoolKind,
  type TrainEdgeId,
  type TroopCost,
  type TroopTypeId,
} from "./troops.ts";
import { describeQuest, isQuestOpen } from "./quest.ts";
import {
  SPAWN_UNTIL_POP,
  canClaimTile,
  canSpawnDaughter,
  claimRadius,
  claimSlotCount,
  encourageGrowthCost,
  formatTier,
  hungerTag,
  settlementPop,
  settlementSeats,
  townFoodProduction,
  eligibleClaimKeys,
  type GrowthState,
} from "./growth.ts";
import {
  effectiveMoveCost,
  formatDefenseMod,
  formatFoodRange,
  formatMoveCost,
  formatWoodYield,
  isSpringRiverBlocked,
  mountainPassNote,
  tileStatsFor,
} from "./tileStats.ts";
import { APP_VERSION, PHASE_LABEL } from "./version.ts";
import { defaultSettings } from "./settings.ts";
import { getSharedWebglBoard } from "./webglBoard.ts";

/** Light demesne ring: hexes within 1 of a selected political keep. */
function hexNearPolitical(_map: HexMap, keepKey: string, key: string): boolean {
  if (key === keepKey) return true;
  const a = parseKey(keepKey);
  const b = parseKey(key);
  return hexDistance(a.q, a.r, b.q, b.r) <= 1;
}

/** Pointy-top axial → pixel */
export function axialToPixel(
  q: number,
  r: number,
  size: number,
): { x: number; y: number } {
  const x = size * (Math.sqrt(3) * q + (Math.sqrt(3) / 2) * r);
  const y = size * ((3 / 2) * r);
  return { x, y };
}

function hexCorner(cx: number, cy: number, size: number, i: number): { x: number; y: number } {
  const angle = ((60 * i - 30) * Math.PI) / 180;
  return { x: cx + size * Math.cos(angle), y: cy + size * Math.sin(angle) };
}

function hexPath(cx: number, cy: number, size: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 6; i++) {
    const p = hexCorner(cx, cy, size, i);
    pts.push(`${p.x.toFixed(2)},${p.y.toFixed(2)}`);
  }
  return pts.join(" ");
}

export interface RenderState {
  map: HexMap;
  clock: ClockState;
  ledger: LedgerState;
  call: CallState;
  growth: GrowthState;
  selectedKey: string | null;
  hexSize: number;
  toast: string | null;
}

function stockClass(id: ResourceId, n: number): string {
  if (id === "food" && n <= 8) return "stock low";
  if (n <= 2) return "stock low";
  return "stock";
}

function toneClass(tone: "green" | "yellow" | "red"): string {
  return `tone tone-${tone}`;
}

export function renderApp(
  root: HTMLElement,
  state: RenderState,
  handlers: {
    onEndTurn: () => void;
    onSelect: (key: string | null) => void;
    onNewSeed: () => void;
    onAcceptOath: () => void;
    onCallBanners: () => void;
    /** full/join · delay · refuse direct; partial may pass troops override from modal */
    onAnswerLiege: (choice: CallOutcome, troopsSentOverride?: number) => void;
    onDismissToast: () => void;
    onToggleClaim: (key: string) => void;
    onEncourageGrowth: () => void;
    onPromoteSeat: () => void;
    onSpawnVillage: () => void;
    onConscript: (n: number) => void;
    onDischarge: (n: number) => void;
    onReleaseHost: () => void;
    /** Train/promote along a TRAIN_EDGES id (player seat only). */
    onPromoteTroops: (edgeId: TrainEdgeId, qty: number, pool: PoolKind) => void;
    /** Voluntary demote reverse of a train edge; refund ⌊cost×0.5⌋. */
    onDemoteTroops: (edgeId: TrainEdgeId, qty: number, pool: PoolKind) => void;
    onMarkHornRead: (id: string) => void;
    onMarkAllHornsRead: () => void;
  },
): void {
  const { map, clock, ledger, call, growth, selectedKey, hexSize, toast } = state;
  const season = seasonOf(clock);
  const liegeLoyalty = loyaltyBucket(call.liege.favor, {
    food: ledger.stocks.food,
    gold: ledger.stocks.gold,
  });
  const calledThisTurn = alreadyCalledBannersThisTurn(call, clock.turn);
  const leviedThisTurn = alreadyLeviedThisTurn(call, clock.turn);
  const armsTotals = seatArmsTotals(call);
  const seatByKey = new Map(call.bannerSeats.map((s) => [s.key, s]));
  const claimed = new Set(growth.seat.claimedKeys);
  for (const b of growth.banners) {
    for (const k of b.claimedKeys) claimed.add(k);
  }
  const seatTown = growth.seat;
  const slots = claimSlotCount(seatTown.population);
  const radius = claimRadius(seatTown.population);
  const usedClaims = seatTown.claimedKeys.length;
  const foodProd = townFoodProduction(map, seatTown);
  // Hunger mouths = seat settlement + garrison + host (banner mouths on own tiles later)
  const seatMouths =
    settlementPop(seatTown) +
    armsTotals.playerGarrison +
    armsTotals.playerHost;
  const hunger = hungerTag(ledger.stocks.food, seatMouths);
  const nets = seasonNetsWithGrowth(season, map, growth, {
    garrison: armsTotals.playerGarrison,
    host: armsTotals.playerHost,
    bannersGarrisoned: armsTotals.bannersGarrisoned,
    bannersInTransit: armsTotals.bannersInTransit,
  });
  const bareNets = seasonNets(season); // keep referenced for tests/compat
  void bareNets;
  const depositYields = realmResourceFlagYields(map, growth);
  const encCost = encourageGrowthCost(seatTown.population);
  const canEncourage =
    ledger.stocks.food >= encCost && seatTown.growthPush <= 0;
  const encourageTitle = seatTown.growthPush > 0
    ? "Growth already encouraged this season"
    : ledger.stocks.food < encCost
      ? `Need ${encCost} food (have ${ledger.stocks.food})`
      : `Spend ${encCost} food to nudge +1 pop next growth tick`;
  const occupiedSeats = settlementSeats(growth, map);

  // Bounds for viewBox
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const c of map.cells.values()) {
    const { x, y } = axialToPixel(c.q, c.r, hexSize);
    minX = Math.min(minX, x - hexSize);
    minY = Math.min(minY, y - hexSize);
    maxX = Math.max(maxX, x + hexSize);
    maxY = Math.max(maxY, y + hexSize);
  }
  const pad = hexSize;
  const vb = `${minX - pad} ${minY - pad} ${maxX - minX + pad * 2} ${maxY - minY + pad * 2}`;

  // Routes as cubic meanders (dedupe undirected) — mirror WebGL chrome
  const drawn = new Set<string>();
  const routeLines: string[] = [];
  const roadWander = hexSize * 0.72;
  const pathHash = (seed: string): number => {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) {
      h ^= seed.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return ((h >>> 0) % 2001) / 1000 - 1;
  };
  /** Dual control points → smooth cubic; shared by roads + rivers. */
  const pathCubic = (
    ax: number,
    ay: number,
    bx: number,
    by: number,
    seed: string,
    amount: number,
  ): { c1x: number; c1y: number; c2x: number; c2y: number } => {
    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const px = -uy;
    const py = ux;
    const side = pathHash(seed);
    const side2 = pathHash(seed + "|s2");
    const along1 = pathHash(seed + "|a1") * 0.2;
    const along2 = pathHash(seed + "|a2") * 0.2;
    const lobe = pathHash(seed + "|l") * 0.28;
    const mag1 = amount * (0.72 + Math.abs(side) * 0.38 + Math.abs(lobe) * 0.2);
    const mag2 = amount * (0.72 + Math.abs(side2) * 0.38 + Math.abs(lobe) * 0.15);
    const s1 = side >= 0 ? 1 : -1;
    // Fillet: same-side wrap around the shared hex vertex — no S-flip through the corner.
    const t1 = 0.22 + along1 * 0.1;
    const t2 = 0.78 + along2 * 0.1;
    const fillet = amount * 0.36;
    return {
      c1x: ax + dx * t1 + px * (mag1 + fillet) * s1 * 0.92,
      c1y: ay + dy * t1 + py * (mag1 + fillet) * s1 * 0.92,
      c2x: ax + dx * t2 + px * (mag2 + fillet) * s1 * 0.92,
      c2y: ay + dy * t2 + py * (mag2 + fillet) * s1 * 0.92,
    };
  };
  for (const c of map.cells.values()) {
    const a = axialToPixel(c.q, c.r, hexSize);
    for (const rk of c.routes) {
      const edge = [cellKey(c.q, c.r), rk].sort().join("|");
      if (drawn.has(edge)) continue;
      drawn.add(edge);
      const o = parseKey(rk);
      const b = axialToPixel(o.q, o.r, hexSize);
      const { c1x, c1y, c2x, c2y } = pathCubic(
        a.x,
        a.y,
        b.x,
        b.y,
        edge + "|rd",
        roadWander,
      );
      routeLines.push(
        `<path d="M ${a.x.toFixed(2)} ${a.y.toFixed(2)} C ${c1x.toFixed(2)} ${c1y.toFixed(2)} ${c2x.toFixed(2)} ${c2y.toFixed(2)} ${b.x.toFixed(2)} ${b.y.toFixed(2)}" class="route" />`,
      );
    }
  }

  // Rivers: cubic meander + season flood width / winter ice class
  const drawnRiv = new Set<string>();
  const riverLines: string[] = [];
  const riverWander = hexSize * 0.82;
  const floodMul = season === "Spring" ? 1.5 : 1;
  const riverSeasonCls =
    season === "Winter" ? " river-ice" : season === "Spring" ? " river-flood" : "";
  for (const c of map.cells.values()) {
    if (c.biome !== "river") continue;
    const a = axialToPixel(c.q, c.r, hexSize);
    for (const [nq, nr] of neighbors(c.q, c.r)) {
      const o = map.cells.get(cellKey(nq, nr));
      if (!o) continue;
      if (o.biome !== "river" && o.biome !== "water") continue;
      const edge = [cellKey(c.q, c.r), cellKey(nq, nr)].sort().join("|");
      if (drawnRiv.has(edge)) continue;
      drawnRiv.add(edge);
      const b = axialToPixel(o.q, o.r, hexSize);
      const { c1x, c1y, c2x, c2y } = pathCubic(a.x, a.y, b.x, b.y, edge, riverWander);
      const widthMul = 1 + 0.15 * pathHash(edge + "|w");
      const sw = (2.9 * widthMul * floodMul).toFixed(2);
      riverLines.push(
        `<path d="M ${a.x.toFixed(2)} ${a.y.toFixed(2)} C ${c1x.toFixed(2)} ${c1y.toFixed(2)} ${c2x.toFixed(2)} ${c2y.toFixed(2)} ${b.x.toFixed(2)} ${b.y.toFixed(2)}" class="river-line${riverSeasonCls}" style="stroke-width:${sw}" />`,
      );
    }
  }

  // P3.7: when seat or a settlement is selected, show claimable-cell boundary ring
  // occupiedSeats already computed above for claim UI
  let boundaryKeys = new Set<string>();
  if (selectedKey) {
    const selCell = map.cells.get(selectedKey);
    if (selCell && selCell.settlement !== "none") {
      const town =
        selectedKey === growth.seat.key
          ? growth.seat
          : growth.banners.find((b) => b.key === selectedKey) ?? growth.seat;
      boundaryKeys = new Set(eligibleClaimKeys(map, town, occupiedSeats));
      // also ring already-claimed tiles for that town
      for (const k of town.claimedKeys) boundaryKeys.add(k);
    }
  }

  const armyAtKey = call.activeQuest?.army.atKey ?? null;
  const armyObjKey = call.activeQuest?.objectiveKey ?? null;
  const hexes: string[] = [];
  for (const c of map.cells.values()) {
    const { x, y } = axialToPixel(c.q, c.r, hexSize);
    const key = cellKey(c.q, c.r);
    const sel = key === selectedKey ? " selected" : "";
    const settle =
      c.settlement === "seat"
        ? " seat"
        : c.settlement === "bannerman"
          ? " bannerman"
          : c.settlement === "political" && c.seatRole
            ? ` political seat-${c.seatRole}`
            : "";
    const claimCls = claimed.has(key) ? " claimed" : "";
    const boundaryCls = boundaryKeys.has(key) ? " claim-boundary" : "";
    // Light demesne outer ring: player seat claims + selected political keep ring
    const demesneCls =
      (key === map.seatKey || (claimed.has(key) && c.settlement === "none" && boundaryKeys.has(key)))
        ? " demesne-player"
        : selectedKey &&
            map.cells.get(selectedKey)?.settlement === "political" &&
            map.cells.get(selectedKey)?.seatRole &&
            hexNearPolitical(map, selectedKey, key)
          ? ` demesne-${map.cells.get(selectedKey)!.seatRole}`
          : "";
    const floodCls =
      isSpringRiverBlocked(c.biome, season, false) ? " flooded" : "";
    const passCls = c.mountainPass ? " mountain-pass" : "";
    const flag = c.resourceFlag;
    const flagGlyph =
      flag === "iron" ? "Fe" : flag === "gold" ? "Au" : flag === "horses" ? "Ho" : "";
    const flagCls = flag ? ` deposit deposit-${flag}` : "";
    const roleIcon =
      c.settlement === "political" && c.seatRole === "liege"
        ? "♛"
        : c.settlement === "political" && c.seatRole === "peer"
          ? "♦"
          : c.settlement === "political" && c.seatRole === "foe"
            ? "▲"
            : "";
    // River hexes paint as cool lowland under the centerline (not full-hex water fill)
    const fillColor =
      c.biome === "river"
        ? "#5a7a5e"
        : BIOME_COLORS[c.biome];
    hexes.push(
      `<g class="hex${sel}${settle}${claimCls}${boundaryCls}${demesneCls}${floodCls}${passCls}${flagCls}" data-key="${key}" style="--fill:${fillColor}">` +
        `<polygon points="${hexPath(x, y, hexSize * 0.95)}" />` +
        (claimed.has(key) && c.settlement === "none"
          ? `<circle cx="${x}" cy="${y + hexSize * 0.35}" r="${hexSize * 0.12}" class="claim-dot" />`
          : "") +
        (flagGlyph
          ? `<text x="${x + hexSize * 0.42}" y="${y - hexSize * 0.28}" class="deposit-mark deposit-${flag}" title="${flag} deposit">${flagGlyph}</text>`
          : "") +
        (c.biome === "forest"
          ? `<text x="${x - hexSize * 0.38}" y="${y - hexSize * 0.32}" class="forest-wood-mark" title="+5–10 wood when claimed">+W</text>`
          : c.biome === "hills" || c.biome === "foothills"
            ? `<text x="${x - hexSize * 0.38}" y="${y - hexSize * 0.32}" class="forest-wood-mark" title="+1–2 wood when claimed">+W</text>`
            : "") +
        (c.mountainPass
          ? `<text x="${x}" y="${y + hexSize * 0.55}" class="pass-mark" title="Seasonal mountain pass">⤒</text>`
          : "") +
        (armyAtKey === key
          ? `<g class="army-token" title="Host on the march"><circle cx="${x}" cy="${y}" r="${hexSize * 0.22}" class="army-disc" /><text x="${x}" y="${y + hexSize * 0.08}" class="army-mark">⚔</text></g>`
          : armyObjKey === key
            ? `<circle cx="${x}" cy="${y}" r="${hexSize * 0.28}" class="quest-objective" />`
            : "") +
        (c.settlement !== "none"
          ? `<circle cx="${x}" cy="${y}" r="${hexSize * 0.28}" class="marker" />`
          : "") +
        (roleIcon
          ? `<text x="${x}" y="${y - hexSize * 0.42}" class="seat-role-icon seat-role-${c.seatRole}" title="${c.seatRole}">${roleIcon}</text>`
          : "") +
        (c.settlement === "seat"
          ? `<text x="${x}" y="${y + 4}" class="label">Seat</text>`
          : c.settlement === "bannerman"
            ? `<text x="${x}" y="${y + 4}" class="label">${escapeXml(c.name?.slice(0, 4) ?? "Ban")}</text>`
            : c.settlement === "political"
              ? `<text x="${x}" y="${y + 4}" class="label">${escapeXml((c.name ?? c.seatRole ?? "Keep").slice(0, 4))}</text>`
              : "") +
        `</g>`,
    );
  }

  const selected = selectedKey ? map.cells.get(selectedKey) : null;
  const seat = ledger.seat;
  const selStats = selected ? tileStatsFor(selected.biome) : null;
  const selClaimed = selectedKey ? growth.seat.claimedKeys.includes(selectedKey) : false;
  const selPassNote = selected ? mountainPassNote(season, selected.mountainPass) : null;
  const selMove = selected
    ? effectiveMoveCost(selected.biome, season, { mountainPass: selected.mountainPass })
    : null;

  const stocksHtml = RESOURCE_IDS.map((id) => {
    const n = ledger.stocks[id];
    const d = nets[id];
    const deltaClass =
      d > 0 ? "res-delta pos" : d < 0 ? "res-delta neg" : "res-delta flat";
    return `<li class="${stockClass(id, n)}" data-res="${id}" title="${RESOURCE_LABELS[id]} stock ${n}; this season ${formatNetDelta(d)}">` +
      `<span class="res-name">${RESOURCE_LABELS[id]}</span>` +
      `<span class="res-val">${n}</span>` +
      `<span class="${deltaClass}" aria-label="season net">${formatNetDelta(d)}</span>` +
      `</li>`;
  }).join("");

  
  const bannersHtml =
    ledger.bannermen.length === 0
      ? `<p class="muted">No bannermen this seed.</p>`
      : `<ul class="banner-list">${ledger.bannermen
          .map((b) => {
            const last = call.lastAnswers.find((a) => a.key === b.key);
            const seatArms = seatByKey.get(b.key) ?? {
              key: b.key,
              name: b.name,
              garrisoned: BANNER_BASE_GARRISON,
              sent: 0,
              inTransit: 0,
            };
            const lastLine = last
              ? `<div class="banner-answer ${last.outcome}">Last: ${OUTCOME_LABELS[last.outcome]} (+${last.troopsSent}) — ${escapeXml(last.reason)}</div>`
              : "";
            // Always show MAA numbers (fallback base garrison if seat not yet wired)
            const maaLine = `<div class="banner-maa" title="Per-seat men-at-arms (garrison holds the seat until called)">MAA · garr <strong>${seatArms.garrisoned}</strong> · sent <strong>${seatArms.sent}</strong> · en route <strong>${seatArms.inTransit}</strong></div>`;
            return `<li class="banner-card" data-key="${escapeXml(b.key)}">
            <div class="banner-head"><strong>${escapeXml(b.name)}</strong>
              <span class="stance stance-${b.tradeStance}">${b.tradeStance}</span>
            </div>
            <div class="banner-meta muted">${escapeXml(b.yieldHint)} · loyalty ${b.loyalty}</div>
            <div class="banner-instruct">Instruct: ${escapeXml(b.instruct)}</div>
            ${maaLine}
            ${lastLine}
          </li>`;
          })
          .join("")}</ul>`;

  const tickHtml = ledger.lastTickLog
    .map((line) => `<li>${escapeXml(line)}</li>`)
    .join("");

  const callLogHtml =
    call.lastCallLog.length === 0
      ? `<p class="muted">No muster yet — swear the oath, then sound the horn.</p>`
      : `<ul class="tick-log call-log">${call.lastCallLog
          .map((line) => `<li>${escapeXml(line)}</li>`)
          .join("")}</ul>`;

  const hornInbox = call.hornInbox ?? { entries: [], nextId: 1 };
  const hornUnread = unreadHornCount(hornInbox);
  const hornPending = pendingHornCount(hornInbox);
  const pendingReplies = call.pendingBannerReplies ?? [];
  const hornEntriesHtml =
    hornInbox.entries.length === 0
      ? `<p class="muted tiny">No horns yet — oath, banner call, and liege answers land here.</p>`
      : `<ul class="horn-list">${hornInbox.entries
          .slice(0, 16)
          .map((e: HornEntry) => {
            const flags = [
              e.read ? "" : "unread",
              e.pending ? "pending" : "",
            ]
              .filter(Boolean)
              .join(" ");
            const meta = [
              `t${e.turn}`,
              e.season ?? "",
              e.kind,
              e.pending && e.turnsLeft != null ? `${e.turnsLeft}t left` : "",
            ]
              .filter(Boolean)
              .join(" · ");
            return `<li class="horn-entry ${flags}" data-horn-id="${escapeXml(e.id)}">
              <button type="button" class="horn-mark" data-horn-id="${escapeXml(e.id)}" title="${e.read ? "Already read" : "Mark read"}"${e.read ? " disabled" : ""}>${e.read ? "✓" : "•"}</button>
              <div class="horn-body">
                <div class="horn-title">${escapeXml(e.title)}</div>
                <div class="horn-text">${escapeXml(e.body)}</div>
                <div class="horn-meta muted tiny">${escapeXml(meta)}</div>
              </div>
            </li>`;
          })
          .join("")}</ul>`;
  const pendingBannerHtml =
    pendingReplies.length === 0
      ? ""
      : `<p class="muted tiny horn-pending-line">En route: ${pendingReplies
          .map(
            (p) =>
              `${escapeXml(p.name)} ${p.committed} (${p.turnsLeft}t · ${p.seasonAtCall})`,
          )
          .join(" · ")}</p>`;
  const hornInboxHtml = `<div class="horn-inbox" role="region" aria-label="Horn inbox">
      <div class="horn-head">
        <span><strong>Horn inbox</strong>${
          hornUnread > 0 ? ` · <span class="horn-badge">${hornUnread} new</span>` : ""
        }${hornPending > 0 ? ` · <span class="muted">${hornPending} open delay</span>` : ""}</span>
        <button type="button" class="btn tiny" id="btn-horns-read-all" ${hornUnread === 0 ? "disabled" : ""} title="Mark all horns read">Mark all read</button>
      </div>
      ${pendingBannerHtml}
      ${hornEntriesHtml}
    </div>`;

  const answerChoices: CallOutcome[] = ["join", "delay", "partial", "refuse"];
  const garrNow = garrisonTotal(call.arms);
  const hostNow = hostTotal(call.arms);
  const availableNow = Math.max(0, garrNow) + Math.max(0, hostNow);
  const partialDefault =
    availableNow <= 0
      ? 0
      : availableNow === 1
        ? 1
        : Math.max(1, Math.min(availableNow - 1, Math.round(availableNow * 0.5)));

  let liegeAnswerHtml: string;
  if (call.liegeCallPending) {
    const delayNote =
      call.liegeDelayTurnsLeft != null
        ? `<p class="delay-clock"><strong>Delay clock:</strong> ${call.liegeDelayTurnsLeft} turn${call.liegeDelayTurnsLeft === 1 ? "" : "s"} left — then treated as refuse (−${REFUSE_FAVOR_HIT} favor).</p>`
        : "";
    liegeAnswerHtml = `<div class="liege-call pending" role="region" aria-label="Liege call">
        <p><strong>${escapeXml(call.liege.name)}</strong> sounds the horn. Will you ride?</p>
        <p class="muted tiny">Full = all ${availableNow} spears (host ${hostNow} + garrison ${garrNow}; host peels first) · Partial = pick how many (keeps ≥1 if you can) · Delay = ${DEFAULT_DELAY_TURNS} turns · Refuse = 0 spears + favor hit. Your banners are a separate muster.</p>
        ${delayNote}
        <div class="answer-row">
          ${answerChoices
            .map((o) => {
              const label =
                o === "join"
                  ? `${OUTCOME_LABELS[o]} (${availableNow})`
                  : o === "refuse"
                    ? `${OUTCOME_LABELS[o]} (0 · −${REFUSE_FAVOR_HIT} fav)`
                    : o === "delay"
                      ? `${OUTCOME_LABELS[o]} (${DEFAULT_DELAY_TURNS}t)`
                      : OUTCOME_LABELS[o];
              return `<button type="button" class="btn answer answer-${o}" data-answer="${o}">${label}</button>`;
            })
            .join("")}
        </div>
      </div>`;
  } else if (call.lastPlayerAnswer) {
    const pa = call.lastPlayerAnswer;
    liegeAnswerHtml = `<p class="muted liege-idle">You answered <strong>${escapeXml(call.liege.name)}</strong>: ${OUTCOME_LABELS[pa.outcome]}${pa.troopsSent > 0 ? ` (${pa.troopsSent} spears)` : ""} — ${escapeXml(pa.reason)}. No open liege call now. End turn — the horn may sound every few seasons.</p>`;
  } else {
    liegeAnswerHtml = `<p class="muted liege-idle">No open liege call. End turn — ${escapeXml(call.liege.name)} may sound the horn every few seasons. <em>Call your banners</em> musters your vassals (separate horn).</p>`;
  }

  // P4 Slice 3 — quest progress strip (+ clearer return / fail copy)
  const aq = call.activeQuest;
  if (aq) {
    const open = isQuestOpen(aq);
    const returning = aq.status === "returning";
    const cls =
      open
        ? returning
          ? "quest-hud open returning"
          : "quest-hud open"
        : aq.status === "won" || aq.status === "arrived"
          ? "quest-hud won"
          : "quest-hud closed";
    let questHint: string;
    if (returning) {
      const n = Math.max(0, Math.floor(aq.army.strength));
      questHint = `Returning ${n} spears: ${aq.army.atKey} → seat ${aq.fromKey}. End turn to march. On arrival they join <strong>host</strong> — then <strong>Release host</strong> to banners.`;
    } else if (open) {
      questHint = `Column at ${escapeXml(aq.army.atKey)} · deadline T${aq.deadlineTurn} · ${aq.turnsLeft}t left. End turn to march. Field resolves on arrival; survivors march home to host.`;
    } else if (aq.status === "failed") {
      questHint = `${escapeXml(aq.note || "Quest failed.")} Surviving spears (if any) are on <strong>host</strong> — use <strong>Release host</strong> for banners.`;
    } else if (aq.status === "won" || aq.status === "arrived") {
      questHint = `${escapeXml(aq.note || aq.status)} Check <strong>host</strong> for returned spears, then <strong>Release host</strong> to banners.`;
    } else {
      questHint = escapeXml(aq.note || aq.status);
    }
    liegeAnswerHtml += `<div class="${cls}" role="status" aria-label="Liege quest"><p><strong>Quest</strong> — ${escapeXml(describeQuest(aq))}</p><p class="muted tiny">${questHint}</p></div>`;
  }

  const callDisabled = !call.oathAccepted || calledThisTurn;
  const callTitle = !call.oathAccepted
    ? "Swear the oath first"
    : calledThisTurn
      ? "Already called this turn — end turn to sound the horn again"
      : "Muster banners into host only — seat garrison stays home (once per turn)";
  const callBtnLabel = calledThisTurn ? "Called this turn" : "Call your banners";

  const canSpawn = canSpawnDaughter(seatTown);
  const promoteBlock = seatTown.pendingPromoteOrSpawn
    ? `<div class="promote-panel" role="region" aria-label="Promote or spawn">
        <p><strong>${escapeXml(seatTown.name)}</strong> is ready (pop ${seatTown.population}${
          canSpawn
            ? ` · spawn until ${SPAWN_UNTIL_POP}`
            : ` · ≥${SPAWN_UNTIL_POP}, promote only`
        }). ${
          canSpawn
            ? "Promote in place or spawn a steading?"
            : "Seat is large enough — promote in place only."
        }</p>
        <div class="answer-row">
          <button type="button" class="btn primary" id="btn-promote">Promote seat</button>
          ${
            canSpawn
              ? `<button type="button" class="btn" id="btn-spawn">Spawn steading</button>`
              : `<button type="button" class="btn" id="btn-spawn" disabled title="Seat pop ≥ ${SPAWN_UNTIL_POP}">Spawn locked</button>`
          }
        </div>
      </div>`
    : "";

  const hostN = hostTotal(call.arms);
  const garrN = garrisonTotal(call.arms);
  const freeAble = freeAbleSeat(seatTown.ableBodied, call.arms);
  const pendingLevy = call.pendingLevy;
  const levyPending = !!pendingLevy;
  const canConscript = freeAble > 0 && !leviedThisTurn && !levyPending;
  const canDischarge = garrN > 0 && !leviedThisTurn && !levyPending;
  const canReleaseHost = hostN > 0;
  const canOpenLevy = canConscript || canDischarge || canReleaseHost;
  const levyTitle = !canOpenLevy
    ? levyPending
      ? "Levy ordered — end turn to finalize"
      : leviedThisTurn && !canReleaseHost
        ? "Already used levy/dismiss this turn — release host still open when available"
        : leviedThisTurn
          ? "Already used levy/dismiss this turn"
          : "No free able, garrison, or host to manage"
    : "Open levy — order conscript or discharge (finalizes next End turn), or release host";
  const conscriptTitle = canConscript
    ? `Order up to ${freeAble} free able into garrison (able reserved now; garrison next End turn; XOR dismiss, once/turn)`
    : levyPending
      ? "Levy already ordered — end turn to finalize"
      : leviedThisTurn
        ? "Already used levy/dismiss this turn"
        : "No free able-bodied left";
  const dischargeTitle = canDischarge
    ? "Order dismiss garrison home (garrison peeled now; able next End turn; XOR conscript, once/turn)"
    : levyPending
      ? "Levy already ordered — end turn to finalize"
      : leviedThisTurn
        ? "Already used levy/dismiss this turn"
        : "No garrison to discharge";
  const releaseTitle = canReleaseHost
    ? "Release host spears back to banner garrisons (fair share)"
    : "No host to release";

  const depositLine =
    depositYields.arms + depositYields.gold + depositYields.horses > 0
      ? `+${depositYields.arms} arms · +${depositYields.gold}g · +${depositYields.horses} ho`
      : "—";
  const pendingLine = pendingLevy
    ? pendingLevy.kind === "conscript"
      ? ` · +${pendingLevy.amount} garr pending`
      : ` · +${pendingLevy.amount} able pending`
    : leviedThisTurn
      ? " · levy used"
      : "";
  const growthHtml = `<div class="growth-panel" aria-label="Town growth">
      <div class="b0-head">
        <strong class="b0-town">${escapeXml(seatTown.name)}</strong>
        <span class="muted tiny">${formatTier(seatTown.tier)} · pop ${seatTown.population}</span>
      </div>
      <div class="b0-stats" role="group" aria-label="Seat stats">
        <span title="Free able-bodied"><strong>${freeAble}</strong> able${pendingLine}</span>
        <span class="hunger hunger-${hunger}" title="Hunger">${hunger}</span>
        <span title="Claims">${usedClaims}/${slots} · r${radius}</span>
        <span title="Food from land">~${foodProd}/s</span>
        <span title="Deposit yields">${depositLine}</span>
      </div>
      <div class="b0-actions">
        <button type="button" class="btn" id="btn-levy" title="${escapeXml(levyTitle)}" ${canOpenLevy ? "" : "disabled"}>Levy…</button>
        <button type="button" class="btn" id="btn-army" title="Army roster — train, promote, demote (player seat)">Army…</button>
        <button type="button" class="btn" id="btn-encourage" title="${escapeXml(encourageTitle)}" ${canEncourage ? "" : "disabled"}>${canEncourage ? `Encourage (−${encCost}f)` : seatTown.growthPush > 0 ? "Encouraged" : `Encourage (−${encCost}f)`}</button>
      </div>
      ${promoteBlock}
    </div>`;

  let claimDisabled = false;
  let claimLabel = "Claim tile";
  let claimTitle = "Claim this tile for the seat";
  if (selected && selectedKey) {
    if (selClaimed) {
      if (selectedKey === growth.seat.key) {
        claimDisabled = true;
        claimLabel = "Seat hex";
        claimTitle = "Cannot unclaim the seat hex";
      } else {
        claimLabel = "Unclaim tile";
        claimTitle = "Release this tile from seat claims";
      }
    } else if (claimed.has(selectedKey)) {
      claimDisabled = true;
      claimLabel = "Banner claim";
      claimTitle = "Worked by a banner town — seat cannot claim";
    } else if (selected.settlement !== "none") {
      claimDisabled = true;
      claimLabel = "Occupied";
      claimTitle = "Settlement hexes are not claimable";
    } else {
      const check = canClaimTile(map, growth.seat, selectedKey, occupiedSeats);
      if (!check.ok) {
        claimDisabled = true;
        claimLabel = "Claim tile";
        claimTitle = check.reason;
      } else {
        const bonus =
          selected.resourceFlag === "iron"
            ? " · +2 arms/season"
            : selected.resourceFlag === "gold"
              ? " · +3 gold/season"
              : selected.resourceFlag === "horses"
                ? " · +2 horses/season"
                : "";
        claimTitle = `Claim this tile for the seat${bonus}`;
      }
    }
  }
  const depositSel =
    selected?.resourceFlag === "iron"
      ? "Iron (+2 arms when claimed)"
      : selected?.resourceFlag === "gold"
        ? "Gold (+3 gold when claimed)"
        : selected?.resourceFlag === "horses"
          ? "Horses (+2 horses when claimed)"
          : "—";
  const selectedHtml = selected && selectedKey && selStats
    ? `<dl class="kv">
        <dt>Hex</dt><dd>${selected.q}, ${selected.r}</dd>
        <dt>Biome</dt><dd>${BIOME_LABELS[selected.biome] ?? selected.biome}</dd>
        <dt>Move</dt><dd>${formatMoveCost(selMove ?? selStats.moveCost)}</dd>
        <dt>Food</dt><dd>${formatFoodRange(selected.biome)}</dd>
        <dt>Wood</dt><dd>${formatWoodYield(selected.biome)}</dd>
        <dt>Defense</dt><dd>${formatDefenseMod(selStats.defenseMod)}</dd>
        <dt>Deposit</dt><dd>${depositSel}</dd>
        <dt>Hold</dt><dd>${selected.settlement === "none" ? "—" : escapeXml(selected.name ?? selected.settlement)}</dd>
        <dt>Claim</dt><dd>${selClaimed ? "Worked by seat" : claimed.has(selectedKey) ? "Worked (banner)" : "Open"}</dd>
      </dl>
      ${
        selPassNote
          ? `<p class="muted tiny pass-hint">${escapeXml(selPassNote)}</p>`
          : ""
      }
      <div class="answer-row">
        <button type="button" class="btn primary" id="btn-claim" data-key="${escapeXml(selectedKey)}" title="${escapeXml(claimTitle)}" ${claimDisabled ? "disabled" : ""}>${claimLabel}</button>
      </div>
      ${
        claimDisabled && !selClaimed
          ? `<p class="muted tiny claim-hint">${escapeXml(claimTitle)}</p>`
          : ""
      }
      ${
        isSpringRiverBlocked(selected.biome, season, false)
          ? `<p class="flood-warn">Spring flood — river blocked without a bridge.</p>`
          : ""
      }`
    : `<p class="muted">Click a hex to inspect biome stats, deposits, and claim/unclaim.</p>`;

  const levyDefaultConscript =
    freeAble <= 0
      ? 0
      : freeAble === 1
        ? 1
        : Math.max(1, Math.min(freeAble, Math.round(freeAble * 0.25) || 1));
  const levyDefaultDischarge =
    garrN <= 0
      ? 0
      : garrN === 1
        ? 1
        : Math.max(1, Math.min(garrN, Math.round(garrN * 0.25) || 1));

  const crewBound = catapultCrewBound(call.arms);
  const gStacks = call.arms.garrison;
  const hStacks = call.arms.host;
  const stocksNow = ledger.stocks;
  const rosterRows = TROOP_TYPES.map((type) => {
    const g = Math.max(0, Math.floor(gStacks[type] ?? 0));
    const h = Math.max(0, Math.floor(hStacks[type] ?? 0));
    const tot = g + h;
    const promo = promoteEdgesFrom(type);
    const demo = demoteEdgesTo(type);
    const label = TROOP_LABELS[type];
    const promoDisabled = promo.length === 0 || tot <= 0;
    const demoDisabled = demo.length === 0 || tot <= 0;
    const promoTitle = promoDisabled
      ? promo.length === 0
        ? "No promote path from this type"
        : "No troops of this type to promote"
      : `Promote ${label} (garrison or host)`;
    const demoTitle = demoDisabled
      ? demo.length === 0
        ? "No demote path for this type"
        : "No troops of this type to demote"
      : `Demote ${label} — refund ⌊cost×0.5⌋`;
    return `<tr data-troop="${type}">
              <th scope="row">${escapeXml(label)}</th>
              <td class="num">${g}</td>
              <td class="num">${h}</td>
              <td class="num"><strong>${tot}</strong></td>
              <td class="roster-actions">
                <button type="button" class="btn tiny btn-roster-promote" data-troop="${type}" title="${escapeXml(promoTitle)}" ${promoDisabled ? "disabled" : ""}>Promote</button>
                <button type="button" class="btn tiny btn-roster-demote" data-troop="${type}" title="${escapeXml(demoTitle)}" ${demoDisabled ? "disabled" : ""}>Demote</button>
              </td>
            </tr>`;
  }).join("");

  const stockChips = (["food", "gold", "iron", "wood", "horses"] as ResourceId[])
    .map((id) => {
      const n = Math.max(0, Math.floor(stocksNow[id] ?? 0));
      return `<span class="roster-stock" data-res="${id}"><strong>${n}</strong> ${escapeXml(RESOURCE_LABELS[id])}</span>`;
    })
    .join("");

  const rosterModal = `<div class="modal-backdrop roster-modal" id="roster-backdrop" hidden role="dialog" aria-modal="true" aria-labelledby="roster-title">
        <div class="modal roster">
          <h2 id="roster-title">Army roster</h2>
          <p class="muted tiny">Player seat only. Train/promote spends stocks; demote refunds ⌊cost×0.5⌋ per channel (instant, step-only). Catapult peels <strong>5 men-at-arms</strong> per engine (1 chassis + 4 crew). Separate from Levy.</p>
          <p class="roster-summary">Free able <strong id="roster-free">${freeAble}</strong> · Crew MAA bound <strong id="roster-crew">${crewBound}</strong> · Garr <strong>${garrN}</strong> · Host <strong>${hostN}</strong></p>
          <div class="roster-stocks" aria-label="Stocks">${stockChips}</div>
          <div class="roster-table-wrap">
            <table class="roster-matrix" aria-label="Troop stacks">
              <thead>
                <tr>
                  <th scope="col">Type</th>
                  <th scope="col" class="num">Garr</th>
                  <th scope="col" class="num">Host</th>
                  <th scope="col" class="num">Σ</th>
                  <th scope="col">Train</th>
                </tr>
              </thead>
              <tbody>
                ${rosterRows}
              </tbody>
            </table>
          </div>
          <div class="roster-nested" id="roster-nested" hidden>
            <h3 id="roster-nested-title">Promote</h3>
            <p class="muted tiny" id="roster-nested-hint"></p>
            <label class="partial-label" for="roster-edge">Path</label>
            <select id="roster-edge" class="roster-select"></select>
            <label class="partial-label" for="roster-pool">Pool</label>
            <select id="roster-pool" class="roster-select">
              <option value="garrison">Garrison</option>
              <option value="host">Host</option>
            </select>
            <label class="partial-label" for="roster-qty">Quantity</label>
            <div class="partial-row">
              <input type="number" id="roster-qty" min="1" value="1" step="1" inputmode="numeric" />
              <span class="muted tiny" id="roster-cost-preview">—</span>
            </div>
            <div class="partial-actions">
              <button type="button" class="btn ghost" id="btn-roster-nested-cancel">Back</button>
              <button type="button" class="btn primary" id="btn-roster-nested-confirm">Confirm</button>
            </div>
          </div>
          <div class="partial-actions" id="roster-main-actions">
            <button type="button" class="btn ghost" id="btn-roster-cancel">Close</button>
          </div>
        </div>
      </div>`;

  const levyModal = `<div class="modal-backdrop levy-modal" id="levy-backdrop" hidden role="dialog" aria-modal="true" aria-labelledby="levy-title">
        <div class="modal levy">
          <h2 id="levy-title">Levy</h2>
          <p class="muted tiny">Able free pool is settlement-only. Conscript OR discharge shares one action per turn (XOR, no gold). Release host returns spears to banner garrisons — not the free pool.</p>
          <p>Free able <strong id="levy-free">${freeAble}</strong> · Garrison <strong id="levy-garr">${garrN}</strong> · Host <strong id="levy-host">${hostN}</strong>${leviedThisTurn ? " · <em>levy/dismiss already used this turn</em>" : ""}</p>
          <div class="levy-modal-section">
            <label class="partial-label" for="levy-conscript-input">Conscript into garrison</label>
            <div class="partial-row">
              <input type="number" id="levy-conscript-input" min="0" max="${Math.max(0, freeAble)}" value="${levyDefaultConscript}" step="1" inputmode="numeric" ${canConscript ? "" : "disabled"} />
              <button type="button" class="btn" id="btn-levy-conscript" title="${escapeXml(conscriptTitle)}" ${canConscript ? "" : "disabled"}>Conscript</button>
            </div>
          </div>
          <div class="levy-modal-section">
            <label class="partial-label" for="levy-discharge-input">Dismiss garrison → able</label>
            <div class="partial-row">
              <input type="number" id="levy-discharge-input" min="0" max="${Math.max(0, garrN)}" value="${levyDefaultDischarge}" step="1" inputmode="numeric" ${canDischarge ? "" : "disabled"} />
              <button type="button" class="btn" id="btn-levy-discharge" title="${escapeXml(dischargeTitle)}" ${canDischarge ? "" : "disabled"}>Discharge</button>
            </div>
          </div>
          <div class="levy-modal-section">
            <button type="button" class="btn ghost" id="btn-levy-release" title="${escapeXml(releaseTitle)}" ${canReleaseHost ? "" : "disabled"}>Release host to banners</button>
          </div>
          <div class="partial-actions">
            <button type="button" class="btn ghost" id="btn-levy-cancel">Close</button>
          </div>
        </div>
      </div>`;

  const partialModal = `<div class="modal-backdrop partial-modal" id="partial-backdrop" hidden role="dialog" aria-modal="true" aria-labelledby="partial-title">
        <div class="modal partial">
          <h2 id="partial-title">Partial levy</h2>
          <p>Send some spears to <strong>${escapeXml(call.liege.name)}</strong>; keep the rest (host peels first, then garrison).</p>
          <p class="muted tiny">Available now: <strong id="partial-garr">${availableNow}</strong> (host ${hostNow} + garrison ${garrNow}). Leave at least 1 home when you can (pool ≥ 2).</p>
          <label class="partial-label" for="partial-input">Spears to send</label>
          <div class="partial-row">
            <input type="number" id="partial-input" min="0" max="${Math.max(0, availableNow)}" value="${partialDefault}" step="1" inputmode="numeric" />
            <span class="muted" id="partial-remain">keep ${Math.max(0, availableNow - partialDefault)} home</span>
          </div>
          <div class="partial-actions">
            <button type="button" class="btn ghost" id="btn-partial-cancel">Cancel</button>
            <button type="button" class="btn primary" id="btn-partial-send">Send partial</button>
          </div>
        </div>
      </div>`;

  const oathModal = !call.oathAccepted
    ? `<div class="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="oath-title">
        <div class="modal oath">
          <h2 id="oath-title">The Oath</h2>
          <p>You kneel to <strong>${escapeXml(call.liege.name)}</strong>, ${escapeXml(call.liege.title)}.</p>
          <p class="muted">Favor starts at ${call.liege.favor} (${LOYALTY_LABELS[loyaltyBucket(call.liege.favor)]}). Garrison ${garrisonTotal(call.arms)} holds the seat and stays put on Call — host is banner visitors only. Call your banners once per turn — and answer when the liege calls you (full / partial / delay / refuse; host peels first when you ride).</p>
          <p class="muted">Winter roads and far holds excuse delay. Silent random does not. Battle auto-resolve lands in Phase 4; spies in Phase 5.</p>
          <button type="button" class="modal-x" id="btn-oath-x" aria-label="Dismiss">×</button>
          <button type="button" class="btn primary" id="btn-oath">Swear the oath</button>
        </div>
      </div>`
    : "";

  const toastHtml =
    toast || call.lastToast
      ? `<div class="toast" role="status" id="toast">${escapeXml(toast ?? call.lastToast ?? "")}
          <button type="button" class="toast-x" id="btn-toast" aria-label="Dismiss">×</button>
        </div>`
      : "";

  const settings = defaultSettings();
  const useWebglBoard = settings["feature.webglBoard"] !== false;
  const seasonTintOn = settings.seasonTint !== false;
  const edgeFadeOn = settings.mapEdgeFade !== false;
  const reduceMotionPref = (() => {
    const rm = settings.reduceMotion;
    if (rm === true || rm === "on" || rm === "reduce") return true;
    if (rm === false || rm === "off") return false;
    // system (default)
    return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  })();

  // Survive full DOM rebuild: pull GL canvas out before innerHTML nukes the host.
  if (useWebglBoard) {
    try {
      getSharedWebglBoard().detach();
    } catch {
      /* ignore */
    }
  }

  root.innerHTML = `
    <div class="shell map-first season-${season.toLowerCase()}" data-hud="p5" data-board="${useWebglBoard ? "webgl" : "svg"}">
      <div class="map-stage" data-pane="D" aria-label="D · Realm map">
        <div class="map-wrap" id="map-wrap">
          <div class="map-canvas-host" id="map-canvas-host" hidden></div>
          <svg class="map" id="map-svg" viewBox="${vb}" role="img" aria-label="Realm hex map" ${useWebglBoard ? "hidden" : ""}>
            <rect x="${minX - pad}" y="${minY - pad}" width="${maxX - minX + pad * 2}" height="${maxY - minY + pad * 2}" class="map-bg" />
            <g class="rivers">${riverLines.join("")}</g>
            <g class="routes">${routeLines.join("")}</g>
            <g class="hexes">${hexes.join("")}</g>
          </svg>
        </div>
      </div>

      <header class="topbar hud-top" data-pane="A" aria-label="A · Top bar">
        <div class="brand">
          <strong>Call to Arms</strong>
          <span class="muted">${PHASE_LABEL} · v${APP_VERSION}</span>
        </div>
        <div class="clock" aria-live="polite">
          <span class="season-pill" data-season="${season}">${season}</span>
          <span class="year">Year ${clock.year}</span>
          <span class="turn muted">Turn ${clock.turn}</span>
        </div>
        <section class="ledger-bar hud-ledger" data-pane="C" aria-label="C · Resource ledger">
          <ul class="stocks">${stocksHtml}</ul>
          <div class="arms-chip" title="Garrison = seat (Call never peels it) · Host = banner visitors only · banner MAA (garrisoned / sent / en route) · realm spears">
            <span>You garr <strong>${armsTotals.playerGarrison}</strong></span>
            <span>You host <strong>${armsTotals.playerHost}</strong></span>
            <span>Banners garr <strong>${armsTotals.bannersGarrisoned}</strong></span>
            <span>Sent <strong>${armsTotals.bannersSent}</strong></span>
            <span>En route <strong>${armsTotals.bannersInTransit}</strong></span>
            <span>Realm <strong>${armsTotals.realmSpears}</strong></span>
          </div>
          <div class="luxury locked" title="Luxury goods unlock later">Luxury · locked</div>
        </section>
        <div class="actions">
          <button type="button" class="btn ghost" id="btn-menu" title="Menu">Menu</button>
          <button type="button" class="btn ghost" id="btn-seed" title="New map seed">Reseed</button>
          <button type="button" class="btn primary" id="btn-end">End turn →</button>
        </div>
      </header>

      ${toastHtml ? `<div class="toast-slot" aria-live="polite">${toastHtml}</div>` : ""}

      <nav class="dock-bar" data-pane="B" aria-label="Game docks">
        <button type="button" class="dock-btn" data-dock="town" aria-controls="dock-town" aria-expanded="false">Town</button>
        <button type="button" class="dock-btn" data-dock="call" aria-controls="dock-call" aria-expanded="false">Call</button>
        <button type="button" class="dock-btn" data-dock="banners" aria-controls="dock-banners" aria-expanded="false">Banners</button>
        <button type="button" class="dock-btn" data-dock="tick" aria-controls="dock-tick" aria-expanded="false">Tick</button>
        <button type="button" class="dock-btn" data-dock="seat" aria-controls="dock-seat" aria-expanded="false">Seat</button>
        <button type="button" class="dock-btn" data-dock="realm" aria-controls="dock-realm" aria-expanded="false">Realm</button>
      </nav>

      <div class="dock-layer" id="dock-layer">
        <section class="hud-overlay dock-panel" id="dock-town" data-pane="B0" hidden aria-hidden="true" aria-label="Town & claims">
          <div class="overlay-head">
            <h3>Town &amp; claims</h3>
            <button type="button" class="overlay-x" data-dock-close="town" aria-label="Close">×</button>
          </div>
          ${growthHtml}
        </section>

        <section class="hud-overlay dock-panel" id="dock-call" data-pane="E3" hidden aria-hidden="true" aria-label="Call to Arms">
          <div class="overlay-head">
            <h3>Call to Arms</h3>
            <button type="button" class="overlay-x" data-dock-close="call" aria-label="Close">×</button>
          </div>
          <dl class="kv">
            <dt>Liege</dt><dd>${escapeXml(call.liege.name)}</dd>
            <dt>Favor</dt><dd>${call.liege.favor} · <span class="loyalty loyalty-${liegeLoyalty}">${LOYALTY_LABELS[liegeLoyalty]}</span></dd>
            <dt>Arms</dt><dd>Garrison ${garrisonTotal(call.arms)} (seat) · host ${hostTotal(call.arms)} (banners)${armsTotals.bannersInTransit > 0 ? ` · banners en route ${armsTotals.bannersInTransit}` : ""}</dd>
          </dl>
          <div class="cta-actions">
            <button type="button" class="btn primary" id="btn-call" ${callDisabled ? "disabled" : ""} title="${escapeXml(callTitle)}">
              ${callBtnLabel}
            </button>
            ${calledThisTurn ? `<span class="muted tiny once-call-hint">Once per turn — end turn to call again.</span>` : ""}
          </div>
          ${liegeAnswerHtml}
          ${
            call.lastPreview
              ? `<p class="preview muted">${escapeXml(call.lastPreview)}</p>`
              : ""
          }
          ${hornInboxHtml}
          <h4 class="subhead">Last muster</h4>
          ${callLogHtml}
        </section>

        <section class="hud-overlay dock-panel" id="dock-banners" data-pane="E5" hidden aria-hidden="true" aria-label="Bannermen">
          <div class="overlay-head">
            <h3>Bannermen</h3>
            <button type="button" class="overlay-x" data-dock-close="banners" aria-label="Close">×</button>
          </div>
          ${bannersHtml}
        </section>

        <section class="hud-overlay dock-panel" id="dock-tick" data-pane="E6" hidden aria-hidden="true" aria-label="Last tick">
          <div class="overlay-head">
            <h3>Last tick</h3>
            <button type="button" class="overlay-x" data-dock-close="tick" aria-label="Close">×</button>
          </div>
          <ul class="tick-log">${tickHtml}</ul>
        </section>

        <section class="hud-overlay dock-panel" id="dock-seat" data-pane="E-seat" hidden aria-hidden="true" aria-label="Your Seat">
          <div class="overlay-head">
            <h3>Your Seat</h3>
            <button type="button" class="overlay-x" data-dock-close="seat" aria-label="Close">×</button>
          </div>
          <p><strong>${escapeXml(seat.name)}</strong></p>
          <dl class="kv">
            <dt>Title</dt><dd>${escapeXml(seat.title)}</dd>
            <dt>Liege</dt><dd>${escapeXml(seat.liegeHint)}</dd>
            <dt>Garrison</dt><dd>${escapeXml(seat.garrisonHint)}</dd>
          </dl>
          <p class="muted">${escapeXml(seat.note)}</p>
        </section>

        <section class="hud-overlay dock-panel dock-panel-wide" id="dock-realm" data-pane="F" hidden aria-hidden="true" aria-label="Realm">
          <div class="overlay-head">
            <h3>Realm</h3>
            <button type="button" class="overlay-x" data-dock-close="realm" aria-label="Close">×</button>
          </div>
          <p class="muted">Seed <code>${escapeXml(map.seed)}</code> · R${map.radius} · ${map.cells.size} hexes</p>
          <ul class="biome-legend" aria-label="Biome legend">
            ${(["plains","forest","hills","foothills","mountains","marsh","coast","river","water"] as const)
              .map(
                (b) =>
                  `<li><span class="swatch" style="background:${BIOME_COLORS[b]}"></span>${BIOME_LABELS[b]}</li>`,
              )
              .join("")}
          </ul>
        </section>
      </div>

      ${
        selected && selectedKey
          ? `<aside class="hud-overlay inspect-panel" data-pane="E" data-pane-b2="B2" aria-label="Inspect">
          <div class="overlay-head">
            <h3>Selected</h3>
            <button type="button" class="overlay-x" id="btn-inspect-close" aria-label="Close inspect">×</button>
          </div>
          ${selectedHtml}
        </aside>`
          : ""
      }

      ${oathModal}
      ${levyModal}
      ${rosterModal}
      ${partialModal}

      <div class="meta-root" id="meta-pause" hidden aria-hidden="true" role="dialog" aria-modal="true" aria-labelledby="pause-title">
        <div class="meta-page" data-meta="pause">
          <h2 id="pause-title">Paused</h2>
          <p class="meta-sub">The board waits. Resume play, open settings, or return to title.</p>
          <div class="meta-actions">
            <button type="button" class="btn primary" id="btn-meta-resume">Resume</button>
            <button type="button" class="btn" id="btn-meta-settings">Settings</button>
            <button type="button" class="btn" id="btn-meta-save" title="Save hook — thin scaffold">Save (soon)</button>
            <button type="button" class="btn" id="btn-meta-load" title="Load hook — thin scaffold">Load (soon)</button>
            <button type="button" class="btn ghost" id="btn-meta-title">Quit to title</button>
          </div>
          <p class="meta-foot">${PHASE_LABEL} · v${APP_VERSION}</p>
        </div>
      </div>

      <div class="meta-root" id="meta-settings" hidden aria-hidden="true" role="dialog" aria-modal="true" aria-labelledby="settings-title">
        <div class="meta-page" data-meta="settings">
          <h2 id="settings-title">Settings</h2>
          <p class="meta-sub">Registry seats for toggles and ranges. Wiring expands later — values are local for now.</p>
          <ul class="meta-settings-list" id="settings-list">
            <li><span>Season tint</span><span class="muted">${seasonTintOn ? "on" : "off"}</span></li>
            <li><span>Reduce motion</span><span class="muted">${String(settings.reduceMotion ?? "system")}</span></li>
            <li><span>Confirm end turn</span><span class="muted">${settings.confirmEndTurn ? "on" : "off"}</span></li>
            <li><span>Map edge soft fade</span><span class="muted">${settings.mapEdgeFade !== false ? "on" : "off"}</span></li>
            <li><span>WebGL board</span><span class="muted">${useWebglBoard ? "on" : "off"}</span></li>
            <li><span>Map pan / zoom</span><span class="muted">${settings["feature.mapCamera"] !== false ? "on" : "off"}</span></li>
            <li><span>Terrain relief</span><span class="muted">${settings["feature.mapRelief"] !== false ? "on" : "off"}</span></li>
          </ul>
          <div class="meta-actions">
            <button type="button" class="btn primary" id="btn-settings-back">Back</button>
          </div>
          <p class="meta-foot">Feature flags &amp; min-max hooks reserved · not a full options product</p>
        </div>
      </div>

      <div class="meta-root" id="meta-title" hidden aria-hidden="true" role="dialog" aria-modal="true" aria-labelledby="title-heading">
        <div class="meta-page" data-meta="title">
          <h1 id="title-heading">Call to Arms</h1>
          <p class="meta-sub">Seasonal hex realm strategy · swear the oath, answer the horn, grow the seat.</p>
          <div class="meta-actions">
            <button type="button" class="btn primary" id="btn-title-continue">Continue</button>
            <button type="button" class="btn" id="btn-title-new">New realm</button>
            <button type="button" class="btn" id="btn-title-settings">Settings</button>
            <button type="button" class="btn ghost" id="btn-title-credits">Credits</button>
          </div>
          <p class="meta-foot">v${APP_VERSION} · thin title scaffold</p>
        </div>
      </div>

      <div class="meta-root" id="meta-credits" hidden aria-hidden="true" role="dialog" aria-modal="true" aria-labelledby="credits-title">
        <div class="meta-page" data-meta="credits">
          <h2 id="credits-title">Credits</h2>
          <p class="meta-sub">Call to Arms — independent hex strategy. Built in phases; map-first play HUD, parchment meta only.</p>
          <div class="meta-actions">
            <button type="button" class="btn primary" id="btn-credits-back">Back</button>
          </div>
          <p class="meta-foot">${PHASE_LABEL}</p>
        </div>
      </div>
    </div>
  `;

  void toneClass;

  const partialBackdrop = root.querySelector("#partial-backdrop") as HTMLElement | null;
  const partialInput = root.querySelector("#partial-input") as HTMLInputElement | null;
  const partialRemain = root.querySelector("#partial-remain") as HTMLElement | null;

  const syncPartialRemain = () => {
    if (!partialInput || !partialRemain) return;
    const pool = availableNow;
    let n = Math.round(Number(partialInput.value));
    if (!Number.isFinite(n)) n = 0;
    n = Math.max(0, Math.min(pool, n));
    partialInput.value = String(n);
    partialRemain.textContent = `keep ${Math.max(0, pool - n)} home`;
  };

  const openPartialModal = () => {
    if (!partialBackdrop || !partialInput) return;
    const pool = availableNow;
    const def =
      pool <= 0
        ? 0
        : pool === 1
          ? 1
          : Math.max(1, Math.min(pool - 1, Math.round(pool * 0.5)));
    partialInput.max = String(Math.max(0, pool));
    partialInput.value = String(def);
    syncPartialRemain();
    partialBackdrop.hidden = false;
    partialBackdrop.setAttribute("aria-hidden", "false");
    partialInput.focus();
  };

  const closePartialModal = () => {
    if (!partialBackdrop) return;
    partialBackdrop.hidden = true;
    partialBackdrop.setAttribute("aria-hidden", "true");
  };

  if (partialBackdrop) {
    partialBackdrop.hidden = true;
    partialBackdrop.setAttribute("aria-hidden", "true");
  }

  root.querySelector("#btn-end")?.addEventListener("click", () => handlers.onEndTurn());
  root.querySelector("#btn-seed")?.addEventListener("click", () => handlers.onNewSeed());
  root.querySelector("#btn-oath")?.addEventListener("click", () => handlers.onAcceptOath());
  root.querySelector("#btn-call")?.addEventListener("click", () => handlers.onCallBanners());
  root.querySelector("#btn-toast")?.addEventListener("click", () => handlers.onDismissToast());
  root.querySelector("#btn-oath-x")?.addEventListener("click", () => handlers.onDismissToast());
  root.querySelector("#btn-horns-read-all")?.addEventListener("click", () =>
    handlers.onMarkAllHornsRead(),
  );
  root.querySelectorAll(".horn-mark").forEach((el) => {
    el.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const id = (el as HTMLElement).dataset.hornId;
      if (id) handlers.onMarkHornRead(id);
    });
  });
  const levyBackdrop = root.querySelector("#levy-backdrop") as HTMLElement | null;
  const levyConscriptInput = root.querySelector("#levy-conscript-input") as HTMLInputElement | null;
  const levyDischargeInput = root.querySelector("#levy-discharge-input") as HTMLInputElement | null;

  const openLevyModal = () => {
    if (!levyBackdrop) return;
    levyBackdrop.hidden = false;
    levyBackdrop.setAttribute("aria-hidden", "false");
    (canConscript ? levyConscriptInput : canDischarge ? levyDischargeInput : null)?.focus();
  };
  const closeLevyModal = () => {
    if (!levyBackdrop) return;
    levyBackdrop.hidden = true;
    levyBackdrop.setAttribute("aria-hidden", "true");
  };
  if (levyBackdrop) {
    levyBackdrop.hidden = true;
    levyBackdrop.setAttribute("aria-hidden", "true");
  }

  root.querySelector("#btn-levy")?.addEventListener("click", () => openLevyModal());
  root.querySelector("#btn-levy-cancel")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    closeLevyModal();
  });
  root.querySelector("#btn-levy-conscript")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (!levyConscriptInput || !canConscript) return;
    let n = Math.round(Number(levyConscriptInput.value));
    if (!Number.isFinite(n)) n = levyDefaultConscript;
    n = Math.max(0, Math.min(freeAble, n));
    if (n <= 0) return;
    closeLevyModal();
    handlers.onConscript(n);
  });
  root.querySelector("#btn-levy-discharge")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (!levyDischargeInput || !canDischarge) return;
    let n = Math.round(Number(levyDischargeInput.value));
    if (!Number.isFinite(n)) n = levyDefaultDischarge;
    n = Math.max(0, Math.min(garrN, n));
    if (n <= 0) return;
    closeLevyModal();
    handlers.onDischarge(n);
  });
  root.querySelector("#btn-levy-release")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (!canReleaseHost) return;
    closeLevyModal();
    handlers.onReleaseHost();
  });

  // ── Army roster (train / promote / demote) ───────────────────────────────
  const rosterBackdrop = root.querySelector("#roster-backdrop") as HTMLElement | null;
  const rosterNested = root.querySelector("#roster-nested") as HTMLElement | null;
  const rosterNestedTitle = root.querySelector("#roster-nested-title") as HTMLElement | null;
  const rosterNestedHint = root.querySelector("#roster-nested-hint") as HTMLElement | null;
  const rosterEdgeSel = root.querySelector("#roster-edge") as HTMLSelectElement | null;
  const rosterPoolSel = root.querySelector("#roster-pool") as HTMLSelectElement | null;
  const rosterQtyInput = root.querySelector("#roster-qty") as HTMLInputElement | null;
  const rosterCostPreview = root.querySelector("#roster-cost-preview") as HTMLElement | null;
  const rosterMainActions = root.querySelector("#roster-main-actions") as HTMLElement | null;
  type RosterMode = "promote" | "demote";
  let rosterMode: RosterMode = "promote";
  let rosterTroop: TroopTypeId | null = null;

  const openRosterModal = () => {
    if (!rosterBackdrop) return;
    closeRosterNested();
    rosterBackdrop.hidden = false;
    rosterBackdrop.setAttribute("aria-hidden", "false");
  };
  const closeRosterModal = () => {
    if (!rosterBackdrop) return;
    closeRosterNested();
    rosterBackdrop.hidden = true;
    rosterBackdrop.setAttribute("aria-hidden", "true");
  };
  const closeRosterNested = () => {
    if (!rosterNested) return;
    rosterNested.hidden = true;
    if (rosterMainActions) rosterMainActions.hidden = false;
  };
  if (rosterBackdrop) {
    rosterBackdrop.hidden = true;
    rosterBackdrop.setAttribute("aria-hidden", "true");
  }

  const poolCount = (pool: PoolKind, type: TroopTypeId): number => {
    const stacks = pool === "host" ? hStacks : gStacks;
    return Math.max(0, Math.floor(stacks[type] ?? 0));
  };

  const refreshRosterCostPreview = () => {
    if (!rosterEdgeSel || !rosterQtyInput || !rosterCostPreview || !rosterPoolSel) return;
    const edgeId = rosterEdgeSel.value as TrainEdgeId;
    const edge = TRAIN_EDGES.find((e) => e.id === edgeId);
    let qty = Math.round(Number(rosterQtyInput.value));
    if (!Number.isFinite(qty) || qty < 1) qty = 1;
    if (!edge) {
      rosterCostPreview.textContent = "—";
      rosterCostPreview.classList.remove("warn");
      return;
    }
    const pool = (rosterPoolSel.value === "host" ? "host" : "garrison") as PoolKind;
    if (rosterMode === "promote") {
      const haveFrom = poolCount(pool, edge.from);
      const perOut = edgeFromPer(edge) + edgeCrewMaa(edge);
      const maxOut = Math.max(0, Math.floor(haveFrom / Math.max(1, perOut)));
      qty = Math.max(1, Math.min(Math.max(1, maxOut || 1), qty));
      // Allow typing above max so red-out can show shortfall; clamp only display max attr
      rosterQtyInput.max = String(Math.max(1, maxOut));
      const rawQty = Math.round(Number(rosterQtyInput.value));
      const want = Number.isFinite(rawQty) && rawQty >= 1 ? rawQty : 1;
      // Keep user-typed qty for validation (don't force-clamp below shortfall feedback)
      qty = want;
      rosterQtyInput.value = String(qty);
      const cost = scaleCost(edge.cost, qty);
      const afford = canAffordStocks(stocksNow, cost);
      const fromNeed = edgeFromNeed(edge, qty);
      const maaOk = haveFrom >= fromNeed;
      const crew = edgeCrewMaa(edge) * qty;
      const chassis = edgeFromPer(edge) * qty;
      const crewNote =
        crew > 0
          ? ` · ${fromNeed} men-at-arms (${chassis}+${crew} crew; have ${haveFrom})`
          : ` · need ${fromNeed} ${TROOP_LABELS[edge.from]} (have ${haveFrom})`;
      const bits: string[] = [`Cost ${formatTroopCost(cost)}`];
      if (!afford) bits.push("cannot afford stocks");
      if (!maaOk) bits.push(`need ${fromNeed} ${TROOP_LABELS[edge.from]}`);
      bits.push(crewNote);
      rosterCostPreview.textContent = bits.join(" — ");
      rosterCostPreview.classList.toggle("warn", !afford || !maaOk);
    } else {
      const maxHave = poolCount(pool, edge.to);
      qty = Math.max(1, Math.min(maxHave || 1, qty));
      rosterQtyInput.value = String(qty);
      rosterQtyInput.max = String(Math.max(1, maxHave));
      const refund = previewDemoteRefund(edge.id, qty);
      const fromBack = edgeFromNeed(edge, qty);
      const crew = edgeCrewMaa(edge) * qty;
      const crewNote =
        crew > 0
          ? ` · restores ${fromBack} ${TROOP_LABELS[edge.from]} (100% crew)`
          : ` · restores ${fromBack} ${TROOP_LABELS[edge.from]}`;
      rosterCostPreview.textContent = `Refund ${formatTroopCost(refund)}${crewNote}`;
      rosterCostPreview.classList.remove("warn");
    }
  };

  const openRosterNested = (mode: RosterMode, troop: TroopTypeId) => {
    if (!rosterNested || !rosterEdgeSel || !rosterPoolSel || !rosterQtyInput) return;
    rosterMode = mode;
    rosterTroop = troop;
    const edges =
      mode === "promote" ? promoteEdgesFrom(troop) : demoteEdgesTo(troop);
    if (edges.length === 0) return;
    rosterEdgeSel.innerHTML = edges
      .map((e) => {
        const label =
          mode === "promote"
            ? `${TROOP_LABELS[e.from]} → ${TROOP_LABELS[e.to]} (${formatTroopCost(e.cost)})`
            : `${TROOP_LABELS[e.to]} → ${TROOP_LABELS[e.from]} (refund ${formatTroopCost(previewDemoteRefund(e.id, 1))}/ea)`;
        return `<option value="${e.id}">${escapeXml(label)}</option>`;
      })
      .join("");
    // Prefer pool that actually has troops
    const gHave = poolCount("garrison", troop);
    const hHave = poolCount("host", troop);
    rosterPoolSel.value = gHave > 0 ? "garrison" : hHave > 0 ? "host" : "garrison";
    for (const opt of Array.from(rosterPoolSel.options)) {
      const p = opt.value as PoolKind;
      const n = poolCount(p, troop);
      opt.textContent = p === "garrison" ? `Garrison (${n})` : `Host (${n})`;
      opt.disabled = n <= 0;
    }
    rosterQtyInput.value = "1";
    if (rosterNestedTitle) {
      rosterNestedTitle.textContent =
        mode === "promote"
          ? `Promote ${TROOP_LABELS[troop]}`
          : `Demote ${TROOP_LABELS[troop]}`;
    }
    if (rosterNestedHint) {
      rosterNestedHint.textContent =
        mode === "promote"
          ? "Spend stocks along a train edge. Catapult needs 5 men-at-arms per engine (1 + 4 crew)."
          : "Instant step-only demote. Resource refund ⌊cost×0.5⌋; men-at-arms (incl. full crew) restore 100%.";
    }
    rosterNested.hidden = false;
    if (rosterMainActions) rosterMainActions.hidden = true;
    refreshRosterCostPreview();
    rosterQtyInput.focus();
  };

  root.querySelector("#btn-army")?.addEventListener("click", () => openRosterModal());
  root.querySelector("#btn-roster-cancel")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    closeRosterModal();
  });
  root.querySelector("#btn-roster-nested-cancel")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    closeRosterNested();
  });
  rosterBackdrop?.addEventListener("click", (ev) => {
    if (ev.target === rosterBackdrop) closeRosterModal();
  });
  rosterBackdrop?.querySelector(".modal")?.addEventListener("click", (ev) => {
    ev.stopPropagation();
  });
  root.querySelectorAll(".btn-roster-promote").forEach((el) => {
    el.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const t = (el as HTMLElement).dataset.troop as TroopTypeId;
      if (!t) return;
      openRosterNested("promote", t);
    });
  });
  root.querySelectorAll(".btn-roster-demote").forEach((el) => {
    el.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const t = (el as HTMLElement).dataset.troop as TroopTypeId;
      if (!t) return;
      openRosterNested("demote", t);
    });
  });
  rosterEdgeSel?.addEventListener("change", () => refreshRosterCostPreview());
  rosterPoolSel?.addEventListener("change", () => refreshRosterCostPreview());
  rosterQtyInput?.addEventListener("input", () => refreshRosterCostPreview());
  root.querySelector("#btn-roster-nested-confirm")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (!rosterEdgeSel || !rosterPoolSel || !rosterQtyInput) return;
    const edgeId = rosterEdgeSel.value as TrainEdgeId;
    const edge = TRAIN_EDGES.find((e) => e.id === edgeId);
    const pool = (rosterPoolSel.value === "host" ? "host" : "garrison") as PoolKind;
    let qty = Math.round(Number(rosterQtyInput.value));
    if (!Number.isFinite(qty)) qty = 1;
    qty = Math.max(1, qty);
    if (!rosterTroop || !edge) return;
    // Client-side gate: red-out + block close when promote would fail (MAA/stocks)
    if (rosterMode === "promote") {
      const haveFrom = poolCount(pool, edge.from);
      const fromNeed = edgeFromNeed(edge, qty);
      const cost = scaleCost(edge.cost, qty);
      const afford = canAffordStocks(stocksNow, cost);
      const maaOk = haveFrom >= fromNeed;
      if (!maaOk || !afford) {
        refreshRosterCostPreview();
        if (rosterCostPreview) {
          rosterCostPreview.classList.add("warn");
          const reasons: string[] = [];
          if (!maaOk) {
            reasons.push(
              `Need ${fromNeed} ${TROOP_LABELS[edge.from]} in ${pool} (have ${haveFrom})`,
            );
          }
          if (!afford) reasons.push("Not enough stocks");
          rosterCostPreview.textContent = reasons.join(" · ");
        }
        return; // keep modal open — no silent fail
      }
    }
    closeRosterModal();
    if (rosterMode === "promote") handlers.onPromoteTroops(edgeId, qty, pool);
    else handlers.onDemoteTroops(edgeId, qty, pool);
  });
  rosterBackdrop?.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape") {
      ev.preventDefault();
      if (rosterNested && !rosterNested.hidden) closeRosterNested();
      else closeRosterModal();
    }
  });
  levyConscriptInput?.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape") {
      ev.preventDefault();
      closeLevyModal();
    }
    if (ev.key === "Enter") {
      ev.preventDefault();
      (root.querySelector("#btn-levy-conscript") as HTMLButtonElement | null)?.click();
    }
  });
  levyDischargeInput?.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape") {
      ev.preventDefault();
      closeLevyModal();
    }
    if (ev.key === "Enter") {
      ev.preventDefault();
      (root.querySelector("#btn-levy-discharge") as HTMLButtonElement | null)?.click();
    }
  });
  levyBackdrop?.addEventListener("click", (ev) => {
    if (ev.target === levyBackdrop) closeLevyModal();
  });
  levyBackdrop?.querySelector(".modal")?.addEventListener("click", (ev) => {
    ev.stopPropagation();
  });
  root.querySelector("#btn-encourage")?.addEventListener("click", () => handlers.onEncourageGrowth());
  root.querySelector("#btn-promote")?.addEventListener("click", () => handlers.onPromoteSeat());
  root.querySelector("#btn-spawn")?.addEventListener("click", () => handlers.onSpawnVillage());
  root.querySelector("#btn-claim")?.addEventListener("click", () => {
    const key = (root.querySelector("#btn-claim") as HTMLElement | null)?.dataset.key;
    if (key) handlers.onToggleClaim(key);
  });
  root.querySelector("#btn-partial-cancel")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    closePartialModal();
  });
  root.querySelector("#btn-partial-send")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (!partialInput) return;
    let n = Math.round(Number(partialInput.value));
    if (!Number.isFinite(n)) n = partialDefault;
    n = Math.max(0, Math.min(availableNow, n));
    closePartialModal();
    handlers.onAnswerLiege("partial", n);
  });
  partialInput?.addEventListener("input", () => syncPartialRemain());
  partialInput?.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape") {
      ev.preventDefault();
      closePartialModal();
    }
    if (ev.key === "Enter") {
      ev.preventDefault();
      (root.querySelector("#btn-partial-send") as HTMLButtonElement | null)?.click();
    }
  });
  partialBackdrop?.addEventListener("click", (ev) => {
    if (ev.target === partialBackdrop) closePartialModal();
  });
  partialBackdrop?.querySelector(".modal")?.addEventListener("click", (ev) => {
    ev.stopPropagation();
  });
  root.querySelectorAll("[data-answer]").forEach((el) => {
    el.addEventListener("click", () => {
      const o = (el as HTMLElement).dataset.answer as CallOutcome;
      if (!o) return;
      if (o === "partial") {
        openPartialModal();
        return;
      }
      handlers.onAnswerLiege(o);
    });
  });
  const closeAllDocks = () => {
    root.querySelectorAll(".dock-panel").forEach((el) => {
      (el as HTMLElement).hidden = true;
      el.setAttribute("aria-hidden", "true");
    });
    root.querySelectorAll(".dock-btn").forEach((el) => {
      el.setAttribute("aria-expanded", "false");
      el.classList.remove("active");
    });
  };

  root.querySelectorAll(".dock-btn").forEach((el) => {
    el.addEventListener("click", (ev) => {
      ev.preventDefault();
      const id = (el as HTMLElement).dataset.dock;
      if (!id) return;
      const panel = root.querySelector(`#dock-${id}`) as HTMLElement | null;
      if (!panel) return;
      const opening = panel.hidden;
      closeAllDocks();
      if (opening) {
        panel.hidden = false;
        panel.setAttribute("aria-hidden", "false");
        el.setAttribute("aria-expanded", "true");
        el.classList.add("active");
      }
    });
  });

  root.querySelectorAll("[data-dock-close]").forEach((el) => {
    el.addEventListener("click", (ev) => {
      ev.preventDefault();
      closeAllDocks();
    });
  });

  root.querySelector("#btn-inspect-close")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    handlers.onSelect(null);
  });

  const metaIds = ["meta-pause", "meta-settings", "meta-title", "meta-credits"] as const;
  const closeAllMeta = () => {
    for (const id of metaIds) {
      const el = root.querySelector(`#${id}`) as HTMLElement | null;
      if (!el) continue;
      el.hidden = true;
      el.setAttribute("aria-hidden", "true");
    }
  };
  const openMeta = (id: (typeof metaIds)[number]) => {
    closeAllMeta();
    closeAllDocks();
    const el = root.querySelector(`#${id}`) as HTMLElement | null;
    if (!el) return;
    el.hidden = false;
    el.setAttribute("aria-hidden", "false");
  };

  root.querySelector("#btn-menu")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    const menu = root.querySelector("#meta-pause") as HTMLElement | null;
    if (!menu) return;
    if (menu.hidden) openMeta("meta-pause");
    else closeAllMeta();
  });
  root.querySelector("#btn-meta-resume")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    closeAllMeta();
  });
  root.querySelector("#btn-meta-settings")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    openMeta("meta-settings");
  });
  root.querySelector("#btn-meta-save")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    handlers.onDismissToast();
    // Thin save hook — full persistence is later residual
    const toast = root.querySelector("#toast") as HTMLElement | null;
    if (toast) {
      /* keep play toast path for real dismiss; save is stub */
    }
    window.alert("Save hook reserved — local schema ships with a later seat.");
  });
  root.querySelector("#btn-meta-load")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    window.alert("Load hook reserved — serialize ports are scaffold only.");
  });
  root.querySelector("#btn-meta-title")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    openMeta("meta-title");
  });
  root.querySelector("#btn-settings-back")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    openMeta("meta-pause");
  });
  root.querySelector("#btn-title-continue")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    closeAllMeta();
  });
  root.querySelector("#btn-title-new")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    closeAllMeta();
    handlers.onNewSeed();
  });
  root.querySelector("#btn-title-settings")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    openMeta("meta-settings");
  });
  root.querySelector("#btn-title-credits")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    openMeta("meta-credits");
  });
  root.querySelector("#btn-credits-back")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    openMeta("meta-title");
  });
  for (const id of metaIds) {
    root.querySelector(`#${id}`)?.addEventListener("click", (ev) => {
      if (ev.target === ev.currentTarget) {
        if (id === "meta-pause" || id === "meta-settings") closeAllMeta();
      }
    });
  }
  root.addEventListener("keydown", (ev) => {
    if (ev.key !== "Escape") return;
    const anyOpen = metaIds.some((id) => {
      const el = root.querySelector(`#${id}`) as HTMLElement | null;
      return el && !el.hidden;
    });
    if (anyOpen) {
      ev.preventDefault();
      closeAllMeta();
    }
  });

  root.querySelectorAll(".hex").forEach((el) => {
    el.addEventListener("click", () => {
      const key = (el as SVGElement).dataset.key ?? null;
      handlers.onSelect(key);
    });
  });
  root.querySelectorAll(".banner-card").forEach((el) => {
    el.addEventListener("click", () => {
      const key = (el as HTMLElement).dataset.key ?? null;
      if (key) handlers.onSelect(key);
    });
  });

  // P5 WebGL board — mount into host; SVG stays as fallback when flag off / mount fails.
  const canvasHost = root.querySelector("#map-canvas-host") as HTMLElement | null;
  const svgMap = root.querySelector("#map-svg") as SVGElement | null;
  if (useWebglBoard && canvasHost) {
    const board = getSharedWebglBoard();
    board.mount(canvasHost);
    canvasHost.hidden = false;
    if (svgMap) svgMap.setAttribute("hidden", "");
    board.draw({
      map,
      hexSize,
      season,
      selectedKey,
      claimed,
      boundaryKeys,
      armyAtKey,
      armyObjKey,
      seasonTint: seasonTintOn,
      edgeFade: edgeFadeOn,
      reduceMotion: !!reduceMotionPref,
      mapCamera: settings["feature.mapCamera"] !== false,
      mapRelief: settings["feature.mapRelief"] !== false,
    });
    // Light C1 pan/zoom/tilt + click-select (pick stays camera-synced).
    board.bindInput((key) => handlers.onSelect(key));
    if (!board.canvas.isConnected) {
      canvasHost.hidden = true;
      svgMap?.removeAttribute("hidden");
    }
  } else if (svgMap) {
    svgMap.removeAttribute("hidden");
    if (canvasHost) canvasHost.hidden = true;
  }
}

/** Format troop stock cost/refund for UI chips. */
function formatTroopCost(cost: TroopCost): string {
  const parts: string[] = [];
  for (const id of RESOURCE_IDS) {
    const n = cost[id];
    if (typeof n === "number" && n > 0) {
      parts.push(`${n} ${RESOURCE_LABELS[id]}`);
    }
  }
  return parts.length ? parts.join(" · ") : "free";
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
